/**
 * Console guard — stops console.* from keeping application objects alive.
 *
 * Whenever a DevTools / CDP client is attached (DevTools open, automated
 * browser tests, memory profilers), Chrome keeps a handle to every object
 * passed to console.* so it can be expanded later. Logging a FormGroup, a
 * question, a component or a decorator event therefore pins that object and
 * everything it references (whole screens) until the client releases it —
 * retained memory grows on every navigation.
 *
 * When installed, object arguments are replaced with a small, bounded text
 * snapshot taken at call time; strings and other primitives pass through
 * untouched, so format strings (%c, %s, …) keep working. Only own data
 * properties are read (getters are never invoked), and the guard never throws.
 */

const MAX_DEPTH = 2;
const MAX_KEYS = 20;
const MAX_ITEMS = 10;
const MAX_LENGTH = 2000;

const METHODS = ['log', 'info', 'warn', 'error', 'debug', 'trace', 'table', 'dir', 'dirxml'];

function typeName(value: any): string {
  try {
    const ctor = value && value.constructor;
    return (ctor && ctor.name) || 'Object';
  } catch (e) {
    return 'Object';
  }
}

function describe(value: any, depth: number, seen: WeakSet<object>): string {
  if (value === null || value === undefined) return String(value);
  const kind = typeof value;
  if (kind === 'string') return depth === 0 ? value : JSON.stringify(value.length > 200 ? value.slice(0, 200) + '…' : value);
  if (kind === 'number' || kind === 'boolean' || kind === 'bigint') return String(value);
  if (kind === 'symbol') return value.toString();
  if (kind === 'function') return `ƒ ${value.name || 'anonymous'}()`;

  if (seen.has(value)) return '[Circular]';
  if (value instanceof Error) {
    const head = `${value.name}: ${value.message}`;
    const stack = typeof value.stack === 'string' ? value.stack : '';
    return stack.startsWith(head) ? stack : head + (stack ? '\n' + stack : '');
  }
  if (typeof Node !== 'undefined' && value instanceof Node) {
    return value.nodeType === 1 ? `<${(value as Element).tagName.toLowerCase()}>` : `#${value.nodeName}`;
  }
  if (value instanceof Date) return isNaN(value.getTime()) ? 'Invalid Date' : value.toISOString();

  const name = typeName(value);
  if (depth >= MAX_DEPTH) {
    return Array.isArray(value) ? `Array(${value.length})` : (name === 'Object' ? '{…}' : `${name} {…}`);
  }
  seen.add(value);
  try {
    if (Array.isArray(value)) {
      const items = [];
      for (let i = 0; i < Math.min(value.length, MAX_ITEMS); i++) {
        items.push(describe(ownValue(value, String(i)), depth + 1, seen));
      }
      if (value.length > MAX_ITEMS) items.push(`…${value.length - MAX_ITEMS} more`);
      return `[${items.join(', ')}]`;
    }
    if (value instanceof Map || value instanceof Set) {
      return `${name}(${value.size})`;
    }
    const keys = Object.keys(value);
    const parts = [];
    for (let i = 0; i < Math.min(keys.length, MAX_KEYS); i++) {
      parts.push(`${keys[i]}: ${describe(ownValue(value, keys[i]), depth + 1, seen)}`);
    }
    if (keys.length > MAX_KEYS) parts.push(`…${keys.length - MAX_KEYS} more`);
    return `${name === 'Object' ? '' : name + ' '}{${parts.join(', ')}}`;
  } finally {
    seen.delete(value);
  }
}

/** Reads a data property without invoking getters (which may have side effects). */
function ownValue(target: any, key: string): any {
  const desc = Object.getOwnPropertyDescriptor(target, key);
  if (!desc) return undefined;
  return 'value' in desc ? desc.value : '[Getter]';
}

function snapshot(value: any): any {
  if (value === null || (typeof value !== 'object' && typeof value !== 'function')) return value;
  try {
    const text = describe(value, 0, new WeakSet());
    return text.length > MAX_LENGTH ? text.slice(0, MAX_LENGTH) + '…' : text;
  } catch (e) {
    return `[${typeName(value)}]`;
  }
}

/**
 * @param skip methods to leave unwrapped, e.g. ['log'] when console.log is
 *             already a no-op — otherwise every call snapshots its arguments
 *             only for the result to be discarded.
 */
export function installConsoleGuard(target: Console = console, skip: string[] = []): void {
  for (const method of METHODS) {
    if (skip.includes(method)) continue;
    const original = (target as any)[method];
    if (typeof original !== 'function') continue;
    (target as any)[method] = function (...args: any[]) {
      return original.apply(this, args.map(snapshot));
    };
  }
  const assert = target.assert;
  if (typeof assert === 'function') {
    target.assert = function (condition?: boolean, ...args: any[]) {
      return assert.call(this, condition, ...args.map(snapshot));
    };
  }
}
